import { useCallback, useEffect, useMemo, useState } from 'react';
import to from 'await-to-js';
import { useAtom, useAtomValue } from 'jotai';
import { useAPIClientRequest, useAuthenticatedUser } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import type { CampaignReportingAssignedVia } from 'c-sdk';
import type { DashboardConfig } from './dashboardConfig';
import {
    atom_customDashboardConfig,
    atom_selectedDashboardKey,
    normalizeDashboardConfig,
    persistSelectedDashboardKey,
} from './dashboardConfig';

// Nearest assignment first, matching how the API resolves. Only used to pick a sensible
// default; once someone chooses a dashboard explicitly, their choice wins.
const PRECEDENCE: CampaignReportingAssignedVia[] = ['user', 'buyer', 'agency', 'client', 'owner'];

export const LOCAL_SOURCE_KEY = 'local';
export const LOCAL_SOURCE_NAME = 'My dashboard';

export type DashboardSource = {
    key: string;
    name: string;
    kind: 'local' | 'assigned';
    /** Set for assigned sources only. */
    id?: number;
    assignedVia?: CampaignReportingAssignedVia;
};

/**
 * Every dashboard this user can view on the Custom tab, and which one is showing.
 *
 * Two kinds: the dashboard they built themselves (held in localStorage), and any number
 * assigned to them by an admin — directly or through an agency, buyer, brand or media owner.
 * `/user/me` carries the assignments without their payload, already ordered most-recently-touched
 * first, so a config's contents are fetched once, when it is first selected, and cached for the
 * session.
 */
export const useDashboardSources = () => {
    const { user } = useAuthenticatedUser();
    const local = useAtomValue(atom_customDashboardConfig);
    const [selectedKey, setSelectedKey] = useAtom(atom_selectedDashboardKey);
    const { start: readConfig } = useAPIClientRequest(ApiClient.Entities.CampaignReporting.get);

    const [loaded, setLoaded] = useState<Record<number, DashboardConfig>>({});
    const [isLoading, setIsLoading] = useState(false);

    const assignments = useMemo(
        () => (user?.campaign_reporting_configs ?? []).filter(a => a.type === 'dashboard'),
        [user],
    );

    const sources = useMemo<DashboardSource[]>(
        () => [
            // A session dashboard loaded from a saved configuration keeps that name, so the
            // tab reads the same as the thing it came from; an unsaved one is just "mine".
            ...(local != null
                ? [
                      {
                          key: LOCAL_SOURCE_KEY,
                          name: local.name ?? LOCAL_SOURCE_NAME,
                          kind: 'local' as const,
                      },
                  ]
                : []),
            ...assignments.map(a => ({
                key: `assigned:${a.id}`,
                name: a.name,
                kind: 'assigned' as const,
                id: a.id,
                assignedVia: a.assigned_via,
            })),
        ],
        [local, assignments],
    );

    // A remembered choice only holds while that dashboard is still available — an assignment
    // can be withdrawn, and a local dashboard deleted.
    const activeKey = useMemo(() => {
        if (selectedKey != null && sources.some(s => s.key === selectedKey)) return selectedKey;
        if (local != null) return LOCAL_SOURCE_KEY;
        const nearest = PRECEDENCE.map(via => assignments.find(a => a.assigned_via === via)).find(
            a => a != null,
        );
        return nearest ? `assigned:${nearest.id}` : null;
    }, [selectedKey, sources, local, assignments]);

    const active = useMemo(
        () => sources.find(s => s.key === activeKey) ?? null,
        [sources, activeKey],
    );

    useEffect(() => {
        if (active?.kind !== 'assigned' || active.id == null) return;
        if (loaded[active.id] != null) return;

        const { id } = active;
        setIsLoading(true);
        (async () => {
            const [err, res] = await to(readConfig(id));
            setIsLoading(false);
            if (err) return;
            const raw = (res as any)?.data?.data?.config;
            if (!raw) return;
            try {
                const parsed = normalizeDashboardConfig(JSON.parse(raw) as DashboardConfig);
                setLoaded(current => ({ ...current, [id]: parsed }));
            } catch {
                // A config we can't read is left out; the tab shows nothing rather than
                // silently falling back to someone else's dashboard.
            }
        })();
    }, [active, loaded, readConfig]);

    const activeConfig = useMemo<DashboardConfig | null>(() => {
        if (active == null) return null;
        if (active.kind === 'local') return local;
        return active.id != null ? loaded[active.id] ?? null : null;
    }, [active, local, loaded]);

    const select = useCallback(
        (key: string) => {
            setSelectedKey(key);
            persistSelectedDashboardKey(key);
        },
        [setSelectedKey],
    );

    return { sources, active, activeKey, activeConfig, isLoading, select };
};
